import { useState } from 'react';
import { JourneyNodeDetails } from './JourneyNodeDetails';
import { dashboardT } from '../../../../../../lib/messages';
import { JourneySankeyNode } from './JourneySankeyNode';
import {
  JourneyEdgeType,
  JourneyNodeId,
  type JourneyResponse,
  journeyRelatedNodeIds,
} from './journeyFlowModel';
import { journeyNodeColor } from './journeyFlowPresentation';
import { JOURNEY_CHART_HEIGHT, journeySankeyLayout } from './journeySankeyLayout';

export function JourneyFlowCanvas(props: {
  data: JourneyResponse;
  selectedNodeId: JourneyNodeId | null;
  onSelectNode: (id: JourneyNodeId) => void;
}) {
  const [hoveredNodeId, setHoveredNodeId] = useState<JourneyNodeId | null>(null);
  const { nodes, links } = journeySankeyLayout(props.data);
  const stages = [...props.data.stages].sort((a, b) => a.order - b.order);
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  const related = journeyRelatedNodeIds(nodes, props.selectedNodeId);
  const detailNodeId = hoveredNodeId ?? props.selectedNodeId ?? JourneyNodeId.BidRequests;
  const hovered = nodeById.get(detailNodeId);
  const height = JOURNEY_CHART_HEIGHT;
  return (
    <>
      <div className="overflow-x-auto">
        <svg
          onMouseLeave={() => {
            setHoveredNodeId(null);
          }}
          role="img"
          aria-label={dashboardT('journey.chart')}
          viewBox={`0 0 ${String(Math.max(1540, stages.length * 310))} ${String(height)}`}
          className="h-auto w-full min-w-[1250px] sm:min-w-[980px]"
        >
          <title>{dashboardT('journey.chart')}</title>
          <desc>{dashboardT('journey.chartDescription')}</desc>
          {stages.map((stage, column) => (
            <text
              key={stage.id}
              x={[74, 380, 700, 1010, 1320][column] ?? 74 + column * 310}
              y={16}
              fill="var(--muted-foreground)"
              className="max-sm:text-[14px]"
              fontSize="12"
              fontWeight="600"
            >
              {stage.label}
            </text>
          ))}
          <defs>
            {nodes.map((target) => {
              const source = target.parentId ? nodeById.get(target.parentId) : undefined;
              if (!source) return null;
              return (
                <linearGradient
                  key={target.id}
                  id={`journey-${target.id}`}
                  gradientUnits="userSpaceOnUse"
                  x1={source.x + 7}
                  x2={target.x}
                  y1={0}
                  y2={0}
                >
                  <stop offset="0%" stopColor={journeyNodeColor(source)} stopOpacity="0.32" />
                  <stop offset="100%" stopColor={journeyNodeColor(target)} stopOpacity="0.26" />
                </linearGradient>
              );
            })}
          </defs>
          {links.map((link) => {
            const source = nodes.find((node) => node.id === link.sourceId);
            const target = nodes.find((node) => node.id === link.targetId);
            if (!source || !target) return null;
            const measuredFlow = link.value > 0;
            let dash: string | undefined;
            if (link.edgeType === JourneyEdgeType.Diagnostic) dash = '4 4';
            else if (!measuredFlow && link.edgeType !== JourneyEdgeType.UnitTransition)
              dash = '1 4';
            let stroke = measuredFlow ? `url(#journey-${target.id})` : journeyNodeColor(target);
            if (link.transitionBand) stroke = 'none';
            return (
              <path
                key={target.id}
                data-edge-type={target.edgeType}
                data-flow-value={link.value}
                d={link.path}
                fill={link.transitionBand ? `url(#journey-${target.id})` : 'none'}
                stroke={stroke}
                strokeWidth={measuredFlow ? Math.max(1, link.width) : 1}
                strokeDasharray={dash}
                opacity={related.has(source.id) && related.has(target.id) ? 1 : 0.08}
                className="transition-opacity"
              />
            );
          })}
          {nodes.map((node) => (
            <JourneySankeyNode
              key={node.id}
              node={node}
              height={node.markerHeight}
              hoverHeight={Math.min(
                Math.max(34, node.markerHeight + 16),
                (nodes.find((next) => next.stage === node.stage && next.y > node.y)?.y ??
                  Infinity) -
                  node.y -
                  6,
              )}
              active={related.has(node.id)}
              selected={node.id === props.selectedNodeId}
              onSelect={props.onSelectNode}
              onHover={setHoveredNodeId}
            />
          ))}
        </svg>
      </div>
      <div className="mx-4 mb-4 grid">
        {nodes.map((node) => {
          const visible = node.id === hovered?.id;
          return (
            <div
              key={node.id}
              role={visible ? 'region' : undefined}
              aria-label={visible ? dashboardT('journey.chart') : undefined}
              aria-hidden={!visible}
              inert={!visible}
              className={`border-border bg-muted/30 col-start-1 row-start-1 rounded-md border p-3 text-xs ${visible ? '' : 'invisible'}`}
            >
              <JourneyNodeDetails node={node} />
            </div>
          );
        })}
      </div>
    </>
  );
}
